/* eslint-disable perfectionist/sort-object-types */

import { useCallback, useRef } from 'react';

import type { CustomData } from '../../types';
import { type EndStateConfig } from '../core/experience/abstract-experience';
import { UFOExperience } from '../core/experience/experience';
import {
	type ExperiencePerformanceTypes,
	type ExperienceTypes,
} from '../core/experience/experience-types';

type UFOExperienceProps = {
	id: string;
	component: string;
	type: ExperienceTypes;
	performanceType: ExperiencePerformanceTypes;
};

export const useUFOExperience = ({
	id,
	component,
	type,
	performanceType,
}: UFOExperienceProps): {
	start: (startTime?: number) => Promise<void>;
	success: (config?: EndStateConfig) => Promise<boolean | null>;
	failure: (config?: EndStateConfig) => Promise<boolean | null>;
	abort: (config?: EndStateConfig) => Promise<boolean | null>;
	mark: (name: string, timestamp?: number) => void;
	addMetadata: (data: CustomData) => void;
	markFMP: (timestamp?: number) => void;
	markInlineResponse: (timestamp?: number) => void;
} => {
	const ufoExperience = useRef<UFOExperience>(
		new UFOExperience(id, {
			type,
			performanceType,
			platform: { component },
		}),
	);

	const start = useCallback((startTime?: number): Promise<void> => {
		return ufoExperience.current.start(startTime);
	}, []);

	const success = useCallback((config?: EndStateConfig): Promise<boolean | null> => {
		return ufoExperience.current.success(config);
	}, []);

	const mark = useCallback((name: string, timestamp?: number): void => {
		ufoExperience.current.mark(name, timestamp);
	}, []);

	const addMetadata = useCallback((data: CustomData): void => {
		ufoExperience.current.addMetadata(data);
	}, []);

	const markFMP = useCallback((timestamp?: number): void => {
		ufoExperience.current.markFMP(timestamp);
	}, []);

	const markInlineResponse = useCallback((timestamp?: number): void => {
		ufoExperience.current.markInlineResponse(timestamp);
	}, []);

	const failure = useCallback((config?: EndStateConfig): Promise<boolean | null> => {
		return ufoExperience.current.failure(config);
	}, []);

	const abort = useCallback((config?: EndStateConfig): Promise<boolean | null> => {
		return ufoExperience.current.abort(config);
	}, []);

	return { start, success, failure, abort, mark, addMetadata, markFMP, markInlineResponse };
};
